import * as React from 'react';

import { Input } from '@independent-software/typeui/controls/Input';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Form } from '@independent-software/typeui/controls/Form';
import { DataTable } from '@independent-software/typeui/controls/DataTable';
import { Checkbox } from '@independent-software/typeui/controls/Checkbox';
import { Tabs } from '@independent-software/typeui/controls/Tabs';

import { AuthContext } from '../../AuthContext';
import { Query } from '../../services/';
import { System, SystemFactory, User } from '../../resource/';
import { Section } from '../../modules';

interface IProps {
  /** Initial form data. */
  data: User;
  /** Called whenever form changes. */
  onChange: (data: User, forceupdate: boolean) => void;
  /** Called whenever a field validates. Returns validation state for whole form. */
  onValidate: (valid: boolean) => void;
  /**
   * Mark form as dirty. If dirty, it will show error messages for all fields,
   * even pristine fields.
   */
  dirty?: boolean;
}

const UserForm = (props: IProps) => {
  const auth = React.useContext(AuthContext);
  const [systems, setSystems] = React.useState([] as System[]);

  React.useEffect(() => {
    loadSystems();
  }, []);

  const loadSystems = () => {
    SystemFactory.getSome(auth, 0, 999, new Query("name"))
    .then((res) => setSystems(res.items));
  }

  const handleChangeSystem = (systemId: number) => {
    const idx = props.data.systems.indexOf(systemId);
    if(idx != -1) {
      props.data.systems.splice(idx, 1);
    } else {
      props.data.systems.push(systemId);
    }
    props.onChange(props.data, true);
  }

  // Role is a single Keycloak role (user or admin), stored as a one-element array.
  const currentRole = (props.data.roles ?? [])[0] ?? 'user';
  const setRole = (role: string) => {
    props.data.roles = [role];
    props.onChange(props.data, true);
  }

  return (
    <Form
      data={props.data} dirty={props.dirty} onChange={props.onChange} onValidate={props.onValidate}>
      <Tabs underlined>
        <Tabs.Pane label="User data">
          <Section padded>
            <Flex.Columns count={2}>
              <Form.Field
                required={{message: "A username is required."}}
                minLength={{length: 3, message: "Username must have at least 3 characters."}}
                maxLength={{length: 30, message: "Username may not have more than 30 characters."}}
                width={1} label="Username"
                name="name"
                value={props.data.name}
                control={(<Input type="text" placeholder="Name" fluid/>)}
                hint={(<React.Fragment>Please enter a username, e.g.  <code>john.smith</code></React.Fragment>)}/>
              <Form.Field
                required={{message: "Email is required."}}
                email={{message: "Email must be a valid email address."}}
                width={1} label="Email"
                name="email"
                value={props.data.email}
                control={(<Input type="text" placeholder="Email" fluid/>)}
                hint={(<React.Fragment>Please enter the user's email address, e.g.  <code>john.smith@gmail.com</code></React.Fragment>)}/>
            </Flex.Columns>
            <div style={{marginTop: 10}}>
              <label style={{display: 'block', marginBottom: 6, fontWeight: 500}}>Role</label>
              <Checkbox radio checked={currentRole === 'user'} label="User" onChange={() => setRole('user')}/>
              &nbsp;&nbsp;
              <Checkbox radio checked={currentRole === 'admin'} label="Administrator" onChange={() => setRole('admin')}/>
            </div>
          </Section>
        </Tabs.Pane>
        <Tabs.Pane label="Systems">
          <DataTable data={systems}>
            <DataTable.Column weight={1} label="System">{(item: System) => <>
              <Checkbox checked={props.data.systems.includes(item.id)} label={item.name} onChange={() => handleChangeSystem(item.id)}/>
            </>}</DataTable.Column>
          </DataTable>
        </Tabs.Pane>
      </Tabs>
    </Form>
  )
}

export { UserForm }
